import { useState, useEffect } from 'react';
import { getFatWisePrices, deleteFatWisePrice, bulkSaveFatWisePrices } from '../services/firebaseService';
import '../style.css';

function PriceEntry() {
  const [milkType, setMilkType] = useState('cow');
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkConfig, setBulkConfig] = useState({
    startFat: '',
    endFat: '',
    baseAmount: '',
    increment: '0.1',
    amountStep: ''
  });

  useEffect(() => {
    loadPrices();
  }, [milkType]);

  // Set default bulk config based on milk type - force reset on type change
  useEffect(() => {
    if (milkType === 'cow') {
      setBulkConfig({
        startFat: '1.0',
        endFat: '10.0',
        baseAmount: '10',
        increment: '0.1',
        amountStep: '0.50'
      });
    } else {
      setBulkConfig({
        startFat: '4.0',
        endFat: '10.0',
        baseAmount: '40',
        increment: '0.1',
        amountStep: '0.50'
      });
    }
    // Close bulk panel when switching types
    setBulkMode(false);
  }, [milkType]);

  const loadPrices = async () => {
    setLoading(true);
    try {
      const data = await getFatWisePrices(milkType);
      const sorted = data.sort((a, b) => parseFloat(a.fat) - parseFloat(b.fat));
      setPrices(sorted);
    } catch (error) {
      console.error('Error loading prices:', error);
    }
    setLoading(false);
  };

  const handleBulkGenerate = async () => {
    const { startFat, endFat, baseAmount, increment, amountStep } = bulkConfig;
    
    if (!startFat || !endFat || !baseAmount || !increment || !amountStep) {
      alert('Please fill all bulk configuration fields');
      return;
    }

    const start = parseFloat(startFat);
    const end = parseFloat(endFat);
    const base = parseFloat(baseAmount);
    const inc = parseFloat(increment);
    const step = parseFloat(amountStep);

    if (start >= end) {
      alert('Start fat must be less than end fat');
      return;
    }

    const count = Math.round((end - start) / inc) + 1;
    const confirmed = window.confirm(
      `Generate ${count} price entries for ${milkType === 'cow' ? 'Cow' : 'Buffalo'} milk?\n\n` +
      `Fat: ${start} to ${end} (increment: ${inc})\n` +
      `Amount: Starting at ₹${base} (step: ₹${step} per 0.1 fat)`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const entries = [];
      for (let fat = start; fat <= end + 0.001; fat = Math.round((fat + inc) * 100) / 100) {
        const stepsFromBase = Math.round((fat - start) / 0.1);
        const amount = Math.round((base + stepsFromBase * step) * 100) / 100;
        entries.push({ fat, amount });
      }
      
      await bulkSaveFatWisePrices(milkType, entries);
      await loadPrices();
      setBulkMode(false);
    } catch (error) {
      console.error('Error generating prices:', error);
      alert('Failed to generate prices');
    }
    setSaving(false);
  };

  const handleDeleteAll = async () => {
    if (prices.length === 0) return;
    
    const confirmed = window.confirm(
      `Delete ALL ${prices.length} price entries for ${milkType === 'cow' ? 'Cow' : 'Buffalo'} milk?\n\nThis cannot be undone!`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      for (const price of prices) {
        await deleteFatWisePrice(price.id);
      }
      await loadPrices();
    } catch (error) {
      console.error('Error deleting prices:', error);
      alert('Failed to delete all prices');
    }
    setSaving(false);
  };

  const stats = {
    count: prices.length,
    minFat: prices.length ? Math.min(...prices.map(p => p.fat)).toFixed(1) : '-',
    maxFat: prices.length ? Math.max(...prices.map(p => p.fat)).toFixed(1) : '-',
    minAmount: prices.length ? Math.min(...prices.map(p => p.amount)).toFixed(2) : '-',
    maxAmount: prices.length ? Math.max(...prices.map(p => p.amount)).toFixed(2) : '-'
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>💰 Price Entry</h1>
        <p>Manage fat-wise milk prices for accurate billing</p>
      </div>

      {/* Milk Type Selector */}
      <div className="price-type-cards">
        <div 
          className={`price-type-card ${milkType === 'cow' ? 'active' : ''}`}
          onClick={() => setMilkType('cow')}
        >
          <span className="type-icon">🐄</span>
          <div className="type-info">
            <h3>Cow Milk</h3>
            <p>Typically Fat 1.0% - 10.0%</p>
          </div>
          {milkType === 'cow' && <span className="check-mark">✓</span>}
        </div>
        <div 
          className={`price-type-card ${milkType === 'buffalo' ? 'active' : ''}`}
          onClick={() => setMilkType('buffalo')}
        >
          <span className="type-icon">🐃</span>
          <div className="type-info">
            <h3>Buffalo Milk</h3>
            <p>Typically Fat 4.0% - 10.0%</p>
          </div>
          {milkType === 'buffalo' && <span className="check-mark">✓</span>}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="price-stats-row">
        <div className="stat-card">
          <span className="stat-value">{stats.count}</span>
          <span className="stat-label">Total Entries</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.minFat}% - {stats.maxFat}%</span>
          <span className="stat-label">Fat Range</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">₹{stats.minAmount} - ₹{stats.maxAmount}</span>
          <span className="stat-label">Price Range</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="price-actions">
        <button 
          className={`action-btn ${bulkMode ? 'active' : ''}`} 
          onClick={() => setBulkMode(!bulkMode)}
        >
          ⚡ Bulk Generate
        </button>
        <button 
          className="action-btn danger" 
          onClick={handleDeleteAll}
          disabled={prices.length === 0 || saving}
        >
          🗑️ Clear All
        </button>
      </div>

      {/* Bulk Generate Panel */}
      {bulkMode && (
        <div className="bulk-panel card">
          <h3>⚡ Bulk Price Generator</h3>
          <p className="bulk-desc">Automatically generate price entries for a range of fat percentages</p>
          
          <div className="bulk-form">
            <div className="bulk-row">
              <div className="bulk-field">
                <label>Start Fat %</label>
                <input
                  type="number"
                  step="0.1"
                  value={bulkConfig.startFat}
                  onChange={(e) => setBulkConfig({...bulkConfig, startFat: e.target.value})}
                  placeholder="1.0"
                />
              </div>
              <div className="bulk-field">
                <label>End Fat %</label>
                <input
                  type="number"
                  step="0.1"
                  value={bulkConfig.endFat}
                  onChange={(e) => setBulkConfig({...bulkConfig, endFat: e.target.value})}
                  placeholder="5.0"
                />
              </div>
              <div className="bulk-field">
                <label>Fat Increment</label>
                <input
                  type="number"
                  step="0.1"
                  value={bulkConfig.increment}
                  onChange={(e) => setBulkConfig({...bulkConfig, increment: e.target.value})}
                  placeholder="0.1"
                />
              </div>
            </div>
            <div className="bulk-row">
              <div className="bulk-field">
                <label>Base Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={bulkConfig.baseAmount}
                  onChange={(e) => setBulkConfig({...bulkConfig, baseAmount: e.target.value})}
                  placeholder="10"
                />
              </div>
              <div className="bulk-field">
                <label>Amount Step (₹ per 0.1 fat)</label>
                <input
                  type="number"
                  step="0.01"
                  value={bulkConfig.amountStep}
                  onChange={(e) => setBulkConfig({...bulkConfig, amountStep: e.target.value})}
                  placeholder="0.25"
                />
              </div>
            </div>
            
            <div className="bulk-preview">
              <strong>Preview:</strong> Fat {bulkConfig.startFat || '?'} = ₹{bulkConfig.baseAmount || '?'}, 
              Fat {(parseFloat(bulkConfig.startFat || 0) + 0.1).toFixed(1)} = ₹{(parseFloat(bulkConfig.baseAmount || 0) + parseFloat(bulkConfig.amountStep || 0)).toFixed(2)}, ...
            </div>
            
            <div className="bulk-actions">
              <button className="generate-btn" onClick={handleBulkGenerate} disabled={saving}>
                {saving ? 'Generating...' : '🚀 Generate Prices'}
              </button>
              <button className="cancel-btn" onClick={() => setBulkMode(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Price List */}
      <div className="price-list card">
        <h3>
          {milkType === 'cow' ? '🐄 Cow Milk' : '🐃 Buffalo Milk'} 
          <span style={{ fontSize: '0.9rem', color: '#999', marginLeft: 'auto' }}>
            {prices.length} entries
          </span>
        </h3>
        
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading prices...</p>
          </div>
        ) : prices.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📋</span>
            <p>No prices set yet</p>
            <p className="empty-hint">Use Bulk Generate to create price entries</p>
          </div>
        ) : (
          <div className="price-chart-container">
            <div className="price-chart-columns">
              {Array.from({ length: Math.ceil(prices.length / 10) }).map((_, colIndex) => (
                <div key={colIndex} className="price-column">
                  {prices.slice(colIndex * 10, (colIndex + 1) * 10).map((price) => (
                    <div key={price.id} className="price-item">
                      <span className="price-fat">Fat {price.fat.toFixed(1)}</span>
                      <span className="price-amount">₹{price.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PriceEntry;
